/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { User } from '@vsolv/core/users/domain';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Activity } from '@vsolv/packages/activities/domain';
import { InfoCardConfig } from '@vsolv/vectors-ui/info-card';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, ReplaySubject, switchMap, tap } from 'rxjs';
import { ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-timeline-activity',
  templateUrl: './claim-timeline-activity.component.html',
  standalone: false,
})
export class ClaimTimelineActivityComponent {
  constructor(
    private linkSvc: LinksWebService,
    private claimSvc: ClaimService
  ) {}

  @PropertyListener('activity') activity$ = new ReplaySubject<Activity.Model>(1);
  @Input() activity: Activity.Model | null = null;

  @PropertyListener('claim') claim$ = new BehaviorSubject<Claim.Model | null>(null);
  @Input() claim: Claim.Model | null = null;

  @Input() hideLine = false;
  @Input() isCustomerPortal = false;
  @Input() displayType: Link.ObjectType | null = null;

  open = false;

  note = Link.ObjectType.NOTE;
  claimType = Link.ObjectType.CLAIM;
  document = Link.ObjectType.STORAGE_ITEM;
  claimItem = Link.ObjectType.CLAIM_ITEM;
  payment = Link.ObjectType.PAYMENT;

  yellow = '#EAAA08';
  green = '#669F2A';
  blue = '#2970FF';

  leadingText = '';
  trailingText = '';

  badgeText = '';
  badgeColor: ThemeColor | null = null;
  detailText = '';

  object: unknown | null = null;
  type: Link.ObjectType | null = null;
  config: InfoCardConfig | null = null;

  // Type-gated and anchored so free-form user text can't trigger a badge.
  // Patterns match both the legacy bare word and the enriched text.
  badges: {
    type: Link.ObjectType;
    pattern: RegExp;
    badgeText: string;
    badgeColor: ThemeColor;
    leadingText?: string;
    trailingText?: string;
  }[] = [
    { type: Link.ObjectType.CLAIM_ITEM, pattern: /^approved(:|$)/, badgeText: 'approved', badgeColor: 'success' },
    { type: Link.ObjectType.CLAIM_ITEM, pattern: /^rejected(:|$)/, badgeText: 'rejected', badgeColor: 'danger' },
    { type: Link.ObjectType.CLAIM_ITEM, pattern: /^resolved(:|$)/, badgeText: 'resolved', badgeColor: 'success' },
    {
      type: Link.ObjectType.CLAIM_ITEM,
      pattern: /^updated the status to draft for$/,
      badgeText: 'Reviewing',
      badgeColor: 'default',
      leadingText: 'updated the status to ',
      trailingText: 'for',
    },
    { type: Link.ObjectType.PAYMENT, pattern: /^voided\b/, badgeText: 'voided', badgeColor: 'default' },
    { type: Link.ObjectType.PAYMENT, pattern: /\bfailed$/, badgeText: 'failed', badgeColor: 'danger' },
    { type: Link.ObjectType.PAYMENT, pattern: /\bcompleted$/, badgeText: 'completed', badgeColor: 'success' },
  ];

  link$ = combineLatest([this.claim$, this.activity$]).pipe(
    switchMap(async ([claim, activity]) =>
      claim && activity
        ? await this.linkSvc.list(
            { id: claim.id, objectType: Link.ObjectType.CLAIM },
            { owner: { id: activity.id, objectType: Link.ObjectType.ACTIVITY } }
          )
        : null
    ),
    map(data => data?.items ?? []),
    map(links => (links.length > 1 ? links.find(link => !link.claimId_1) : links[0])),
    tap(link => (link ? (this.type = Link.getLinkType(link, Link.ObjectType.ACTIVITY)) : '')),
    tap(link => {
      if (link && this.type) {
        this.object = Link.getLinkObject(link, this.type);
        this.config = Link.getLinkInfoCardConfig(link, this.type);

        this.applyBadge();

        // Make router links
        if (!(this.object as any)?.deleted) {
          if (this.type === Link.ObjectType.CLAIM_ITEM) {
            this.config.routerLink = link.claimItem_1?.id;

            const step = this.getClaimItemStep(this.activity?.text);
            if (step) this.config.routerLink += step;
          } else if (this.type === Link.ObjectType.NOTE) {
            this.config.routerLink = 'notes';
            this.config.queryParams = { noteId: (this.object as any)?.id };
          } else if (this.type === Link.ObjectType.STORAGE_ITEM) {
            this.config.routerLink = 'attachments';
            this.config.queryParams = { documentId: (this.object as any)?.id };
          } else if (this.type === Link.ObjectType.PAYMENT) {
            this.config.routerLink = `payments/${(this.object as any)?.id}`;
          }
        }
      }
    })
  );

  // Anchored on the action prefix - 'secure' contains 'cure', so a substring search picks the wrong step.
  getClaimItemStep(text?: string | null) {
    const action = text?.trim();
    if (!action) return null;

    if (/^(resolved|updated the resolution)\b/.test(action)) return '/resolve';
    if (/^(approved|rejected|updated the (approval|rejection))\b/.test(action)) {
      return this.isCustomerPortal ? null : '/adjudicate';
    }
    if (/^(added the cure|updated the cure|updated the amount)\b/.test(action)) return '/cure';

    return null;
  }

  applyBadge() {
    this.leadingText = '';
    this.trailingText = '';
    this.badgeText = '';
    this.badgeColor = null;
    this.detailText = '';

    const text = this.activity?.text?.trim();
    if (!text || !this.type) return;

    const badge = this.badges.find(candidate => candidate.type === this.type && candidate.pattern.test(text));
    if (!badge) return;

    this.badgeText = badge.badgeText;
    this.badgeColor = badge.badgeColor;
    this.leadingText = badge.leadingText ?? '';
    this.trailingText = badge.trailingText ?? '';
    // keep the detail that follows the status word
    this.detailText = text.replace(badge.pattern, '').replace(/^:\s*/, '').trim();
  }

  getInitials(user?: User.Model | null) {
    if (user) {
      const name = user.displayName?.split(' ');
      if (!name?.length) return 'WS';

      const first = name[0]?.charAt(0);
      const last = name[1]?.charAt(0);
      return (first ?? '') + (last ?? '');
    } else return 'WS';
  }

  getItemTheme(item?: Claim.ClaimItem.Model) {
    return item ? (item.deleted ? 'info' : Claim.ClaimItem.getTheme(item.status)) : 'default';
  }

  getNoteContent(content?: string) {
    return (
      content
        ?.replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .trim() ?? ''
    );
  }

  getTypeColor(type: Link.ObjectType | null) {
    return type === this.note
      ? this.yellow
      : type === this.document
        ? this.green
        : type === this.payment
          ? this.blue
          : '';
  }
}
